import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { queryClient } from '@/lib/query-client';

/**
 * MEGSZEMÉLYESÍTÉS („belépés más nevében") — a felület állapota és a két művelet.
 *
 * ══ AZ ÁLLAPOT FORRÁSA A TOKEN, NEM KLIENS-VÁLTOZÓ ═════════════════════════════════════════════════════
 * Az „épp megszemélyesítek" tényt a `/session` adja (a token `act` claimjéből). Ez SZÁNDÉKOSAN nem egy külön
 * kliens-oldali flag: a megszemélyesítő token rövid életű és NEM újítható meg, tehát ha lejár, a kliens
 * csendben visszaáll az aktor tokenjére (a 401-re futó automatikus refresh a SAJÁT cookie-ból jön). Egy külön
 * flag ilyenkor „ragadva" maradna, és a sáv azt hazudná, hogy még mindig más nevében dolgozunk.
 *
 * ══ MIÉRT ÜRÍTJÜK A TELJES QUERY-CACHE-T ═══════════════════════════════════════════════════════════════
 * A token-váltással a kérések MÁS identitás jogképével mennek. Az invalidálás (refetch) nem elég: a régi
 * adat egy pillanatra még megjelenne az új identitás nézetében — épp az a fajta „ki adatát látom?" zavar,
 * amit ennél a funkciónál a legkevésbé engedhetünk meg. Ezért `queryClient.clear()`, majd navigálás.
 */

/**
 * Az Odin platform-scope-ja, ami a megszemélyesítést engedélyezi. A felület a `hasScope`-pal ezt kéri —
 * a jog-kapu a felületen (lásd `ImpersonateRowAction`) — a kulcs MAGA a platform-képesség, egy pipa a konzolban.
 */
export const IMPERSONATE_SCOPE = 'iam:impersonate';

export interface ImpersonationState {
  /** Aktív megszemélyesítés (a token `act` claimje) — a sáv ebből jelenik meg. */
  active: boolean;
  /** Kit személyesítünk meg (a `/session` szerint). */
  subjectName: string | null;
  /** A cél NÉV-RÉSZEI — a locale-tudatos névsorhoz (hu → „Vezetéknév Keresztnév"), lásd lent. */
  subjectGivenName: string | null;
  subjectFamilyName: string | null;
  subjectEmail: string | null;
  /** A VALÓDI aktor neve (a támogató munkatárs) — a sáv „X néven dolgozol" felirata. */
  actorName: string | null;
  /**
   * Az aktor NÉV-RÉSZEI — ugyanaz a locale-szabály, mint a célnál: a sorrendet a KLIENS dönti.
   * `null`, ha a szerver címtára nem elérhető → a sáv az `actorName`-re esik vissza.
   */
  actorGivenName: string | null;
  actorFamilyName: string | null;
  /** Épp fut a start/stop hívás (a gombok tiltásához). */
  isPending: boolean;
  /** Megszemélyesítés indítása — siker esetén a lap újratöltés NÉLKÜL átvált a cél nézetére. */
  start: (identityId: string, reason?: string) => Promise<void>;
  /** Visszalépés a saját identitásra. */
  stop: () => Promise<void>;
}

export function useImpersonation(): ImpersonationState {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPending, setPending] = useState(false);
  const sessionQ = api.session.useQuery(['session'], {});
  const body = sessionQ.data?.status === 200 ? sessionQ.data.body : undefined;
  const info = body?.impersonation ?? null;

  const start = async (identityId: string, reason?: string): Promise<void> => {
    setPending(true);
    try {
      const res = await api.impersonationStart.mutation({
        body: { identityId, ...(reason === undefined || reason === '' ? {} : { reason }) },
      });
      if (res.status !== 200) {
        // A szerver üzenete FELHASZNÁLÓNAK SZÓL (a BFF fordítja a platform-falak okait) — ezért kiírjuk.
        const message =
          (res.body as { message?: string } | undefined)?.message ?? t('toast:impersonation.error');
        toast.error(message);
        return;
      }
      useAuthStore.getState().setToken(res.body.accessToken);
      // A teljes cache eldobása (lásd a fájl-fejlécet), majd a cél felületére navigálás: partnernél a
      // portál, belsősnél a kezdőlap. A `/session` újratöltése adja a sávot és a friss jogképet.
      queryClient.clear();
      await navigate({ to: res.body.subject.type === 'partner' ? '/portal' : '/' });
      toast.success(
        t('toast:impersonation.started', {
          name: res.body.subject.name ?? res.body.subject.email ?? '',
        }),
      );
    } catch {
      toast.error(t('toast:impersonation.error'));
    } finally {
      setPending(false);
    }
  };

  const stop = async (): Promise<void> => {
    setPending(true);
    try {
      // A `stop` route `c.noBody()` — a kliens ezért body NÉLKÜL hívja (a Fastify oldalon sincs törzs).
      const res = await api.impersonationStop.mutation({});
      const token = res.status === 200 ? res.body.accessToken : null;
      queryClient.clear();
      if (token === null) {
        // KI CSAK AKKOR léptetünk, ha nincs élő AKTOR-munkamenet. Ezt a szerver MONDJA MEG (`reason`), mert
        // a leállítás nem 401-gyel tér vissza: a 200-as válasz `expired`/`transient` ága különbözteti meg a
        // lejárt aktor-cookie-t az átmeneti upstream-hibától. (Korábban csak a — soha be nem következő —
        // 401-re léptettünk ki, ezért a lejárt aktor-munkamenetnél a felhasználó BENT RAGADT a
        // megszemélyesítésben.) Átmeneti hibánál a cookie él: a kiléptetés adatvesztés volna, és a kézi
        // újra-belépés az Odin login-limitjébe (5/15 perc) futna — ugyanaz a szabály, mint a
        // `refreshSession()`-ben (lásd `lib/auth.ts`).
        const expired = res.status === 401 || (res.status === 200 && res.body.reason === 'expired');
        if (!expired) {
          toast.error(t('toast:impersonation.stopFailed'));
          return;
        }
        useAuthStore.getState().logout();
        await navigate({ to: '/login' });
        toast.info(t('toast:impersonation.sessionExpired'));
        return;
      }
      useAuthStore.getState().setToken(token);
      await navigate({ to: '/' });
      toast.success(t('toast:impersonation.stopped'));
    } catch {
      toast.error(t('toast:impersonation.error'));
    } finally {
      setPending(false);
    }
  };

  return {
    active: info !== null,
    subjectName: info?.subjectName ?? null,
    // A NÉV-RÉSZEK a session GYÖKERÉBŐL jönnek, nem az `impersonation` objektumból — és ez nem trükk:
    // megszemélyesítés közben a token `sub`-ja MAGA a cél, tehát a `/session` profil-mezői (`givenName`/
    // `familyName`) a CÉL nevét adják. Enélkül a sáv a nyers `displayName`-et írta ki, a lista pedig a
    // locale-sorrendet — ugyanaz a személy két néven jelent meg („Sales Abbázia" vs. „Abbázia Sales",
    // mérve az e2e-n).
    subjectGivenName: info !== null ? (body?.givenName ?? null) : null,
    subjectFamilyName: info !== null ? (body?.familyName ?? null) : null,
    subjectEmail: info?.subjectEmail ?? null,
    actorName: info?.actorName ?? null,
    actorGivenName: info?.actorGivenName ?? null,
    actorFamilyName: info?.actorFamilyName ?? null,
    isPending,
    start,
    stop,
  };
}
